import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyOrders } from "../../api/client";
import { PACKAGE_TYPE_LABELS } from "../../utils/packageLabels";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import LoadingIndicator from "../../components/LoadingIndicator";

const STATUS_BADGE = {
  requested:          "warning",
  accepted:           "info",
  arrived_at_pickup:  "info",
  picked_up:          "info",
  in_transit:         "info",
  delivered:          "",
  completed:          "",
  cancelled:          "danger",
  failed:             "danger",
  scheduled:          "warning",
};

const STATUS_ICON = {
  requested:         "📡",
  accepted:          "🛵",
  arrived_at_pickup: "📍",
  picked_up:         "📦",
  in_transit:        "🚀",
  delivered:         "✅",
  completed:         "✅",
  cancelled:         "❌",
  failed:            "❌",
  scheduled:         "⏰",
};

const ACTIVE_STATUSES = ["requested", "accepted", "arrived_at_pickup", "picked_up", "in_transit"];

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });
}

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "active" | "completed" | "cancelled"
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const { data } = await getMyOrders();
        setOrders(data);
      } catch (err) {
        setError(err.response?.data?.error || "Could not load your orders.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filteredOrders = orders
    .filter((o) => {
      if (filter === "active") return ACTIVE_STATUSES.includes(o.status);
      if (filter === "completed") return ["delivered", "completed"].includes(o.status);
      if (filter === "cancelled") return ["cancelled", "failed"].includes(o.status);
      return true;
    })
    .filter((o) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        o._id.toLowerCase().includes(q) ||
        o.pickup?.address?.toLowerCase().includes(q) ||
        o.dropoff?.address?.toLowerCase().includes(q) ||
        o.rider?.name?.toLowerCase().includes(q)
      );
    });

  const activeCount = orders.filter((o) => ACTIVE_STATUSES.includes(o.status)).length;

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader backTo="/order" backLabel="New Delivery" />

        <div className="page">
          <div className="brand-header">
            <span className="brand-logo">📋</span>
            <span className="brand-name">My Orders</span>
            <p className="brand-tagline">Track and view all your deliveries</p>
          </div>

          {/* Filter Tabs */}
          <div className="role-tabs" style={{ marginBottom: "20px" }}>
            <button
              className={`role-tab ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All ({orders.length})
            </button>
            <button
              className={`role-tab ${filter === "active" ? "active" : ""}`}
              onClick={() => setFilter("active")}
            >
              🔴 Live ({activeCount})
            </button>
            <button
              className={`role-tab ${filter === "completed" ? "active" : ""}`}
              onClick={() => setFilter("completed")}
            >
              Completed
            </button>
            <button
              className={`role-tab ${filter === "cancelled" ? "active" : ""}`}
              onClick={() => setFilter("cancelled")}
            >
              Cancelled
            </button>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search by order ID, address, or dispatcher name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginBottom: "16px" }}
          />

          {/* Error */}
          {error && <p className="error">{error}</p>}

          {/* Loading */}
          {loading && <LoadingIndicator label="Loading your orders..." />}

          {/* Empty */}
          {!loading && !error && filteredOrders.length === 0 && (
            <div className="empty-state">
              <span className="empty-state-icon">📭</span>
              <p className="empty-state-text">
                {orders.length === 0
                  ? "You haven't placed any orders yet."
                  : search.trim()
                  ? "No orders match your search."
                  : `No ${filter} orders found.`}
              </p>
              {orders.length === 0 && (
                <Link to="/order">
                  <button style={{ marginTop: "12px" }}>Send a Package</button>
                </Link>
              )}
            </div>
          )}

          {/* Orders List */}
          {!loading && filteredOrders.map((order) => {
            const isActive = ACTIVE_STATUSES.includes(order.status);
            return (
              <div
                key={order._id}
                className="delivery-card"
                style={{ marginBottom: "16px", borderLeft: isActive ? "3px solid var(--text-accent)" : undefined }}
              >
                {/* Header Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: "13px", color: "var(--text-secondary)" }}>
                      #{order._id.slice(-6).toUpperCase()}
                    </span>
                    {isActive && (
                      <span style={{ marginLeft: "8px", fontSize: "11px", color: "var(--text-accent)", fontWeight: 700 }}>
                        ● LIVE
                      </span>
                    )}
                  </div>
                  <span className={`status-badge ${STATUS_BADGE[order.status] || ""}`}>
                    {STATUS_ICON[order.status]} {order.status.replace(/_/g, " ").toUpperCase()}
                  </span>
                </div>

                {/* Route */}
                <div className="info-row">
                  <span className="label">📍 Pickup</span>
                  <span className="value">{order.pickup.address}</span>
                </div>
                <div className="info-row">
                  <span className="label">🏁 Drop-off</span>
                  <span className="value">{order.dropoff.address}</span>
                </div>
                <div className="info-row">
                  <span className="label">📦 Package</span>
                  <span className="value">
                    {PACKAGE_TYPE_LABELS[order.packageType] || order.packageType}
                    {order.packageDescription ? ` — ${order.packageDescription}` : ""}
                  </span>
                </div>
                {order.vehicleType === "truck" && order.cargoWeight && (
                  <div className="info-row">
                    <span className="label">📦 Cargo Weight</span>
                    <span className="value">{order.cargoWeight}</span>
                  </div>
                )}

                {/* Rider */}
                {order.rider && (
                  <div className="info-row">
                    <span className="label">🛵 Dispatcher</span>
                    <span className="value">{order.rider.name} · ⭐{order.rider.rating?.toFixed(1) || "New"}</span>
                  </div>
                )}

                {/* Price & Date */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px", alignItems: "center" }}>
                  <span style={{ fontWeight: 700, fontSize: "16px", color: "var(--text-accent)" }}>
                    ₦{order.priceSnapshot?.total?.toLocaleString()}
                  </span>
                  <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                    {formatDate(order.createdAt)}
                  </span>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                  {isActive && (
                    <Link to={`/track/${order._id}`} style={{ flex: 1 }}>
                      <button style={{ width: "100%", padding: "10px" }}>
                        🗺️ Track Live
                      </button>
                    </Link>
                  )}
                  {!isActive && order.status === "completed" && !order.rating && (
                    <Link to={`/track/${order._id}`} style={{ flex: 1 }}>
                      <button className="secondary" style={{ width: "100%", padding: "10px" }}>
                        ⭐ Rate Delivery
                      </button>
                    </Link>
                  )}
                  {!isActive && (
                    <Link to={`/track/${order._id}`} style={{ flex: 1 }}>
                      <button className="secondary" style={{ width: "100%", padding: "10px" }}>
                        📄 View Details
                      </button>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
